<template>
  <b-modal v-model="modal" title="Inserir Usuários em Massa" size="lg" :no-enforce-focus="true" :no-close-on-backdrop="true">
    <loading
      :active.sync="loading"
      color="#556ee6"
      loader="dots"
      :can-cancel="false"
      :is-full-page="false">
    </loading>
    <div class="row">
      <div class="col-lg-12">
        <form @submit.prevent="saveBulk()" autocomplete="off">
          <div class="alert alert-info" role="alert" v-if="!bulkData.role_id">
            <i class="mdi mdi-information mr-2"></i>
            Selecione um tipo de usuário para ver o formato dos emails que serão criados.
          </div>
          <div class="alert alert-info" role="alert" v-else>
            <i class="mdi mdi-information mr-2"></i>
            Os usuários serão criados automaticamente com emails no formato
            <strong>{{ emailExample.first }}</strong>, <strong>{{ emailExample.second }}</strong>, <strong>{{ emailExample.third }}</strong>, etc.
          </div>

          <div class="form-group">
            <label for="role_id">Tipo de Usuário <span class="text-danger">*</span></label>

            <div class="role-options">
              <div
                v-for="role in availableRoles"
                :key="role.id"
                class="role-option"
                :class="{ 'role-selected': bulkData.role_id === role.id }"
                @click="selectRole(role.id)"
              >
                <div class="role-header">
                  <strong>{{ role.label }}</strong>
                </div>
                <p class="role-description">{{ getRoleDescription(role.name) }}</p>
              </div>
            </div>

            <div
              v-if="submitted && !bulkData.role_id"
              class="invalid-feedback d-block"
            >
              O campo tipo de usuário é obrigatório.
            </div>
          </div>

          <div class="form-group">
            <label for="quantity">Quantidade <span class="text-danger">*</span></label>
            <input
              id="quantity"
              v-model.number="bulkData.quantity"
              name="quantity"
              type="number"
              min="1"
              max="100"
              class="form-control"
              placeholder="Digite a quantidade de usuários"
              :class="{
                'is-invalid': submitted && (!bulkData.quantity || bulkData.quantity < 1 || bulkData.quantity > 100)
              }"
            />
            <small class="form-text text-muted">Máximo de 100 usuários por vez</small>
            <div
              v-if="submitted && !bulkData.quantity"
              class="invalid-feedback d-block"
            >
              O campo quantidade é obrigatório.
            </div>
            <div
              v-if="submitted && bulkData.quantity && (bulkData.quantity < 1 || bulkData.quantity > 100)"
              class="invalid-feedback d-block"
            >
              A quantidade deve ser entre 1 e 100.
            </div>
          </div>

          <div class="form-group">
            <label for="password">Senha Provisória <span class="text-danger">*</span></label>
            <input
              id="password"
              v-model="bulkData.password"
              name="password"
              type="password"
              class="form-control"
              placeholder="Digite a senha provisória"
              :class="{
                'is-invalid': submitted && (!bulkData.password || bulkData.password.length < 6)
              }"
            />
            <small class="form-text text-muted">
              <i class="mdi mdi-information-outline mr-1"></i>
              Esta senha provisória será usada para todos os usuários. No primeiro acesso, eles poderão alterá-la.
            </small>
            <div
              v-if="submitted && !bulkData.password"
              class="invalid-feedback d-block"
            >
              O campo senha é obrigatório.
            </div>
            <div
              v-if="submitted && bulkData.password && bulkData.password.length < 6"
              class="invalid-feedback d-block"
            >
              A senha deve ter pelo menos 6 caracteres.
            </div>
          </div>

        </form>
      </div>
    </div>
    <template v-slot:modal-footer>
      <b-button variant="secondary" @click="close()">Cancelar</b-button>
      <button type="submit" class="btn btn-primary mr-1" @click="saveBulk()">Criar Usuários</button>
    </template>
  </b-modal>
</template>
<script>
// Import component
import Loading from 'vue-loading-overlay'
// Import stylesheet
import 'vue-loading-overlay/dist/vue-loading.css'
import { mapActions, mapState } from 'vuex'
import { errorMessage, successMessage } from '@/components/alertsMessages'

export default {
  components: {
    Loading
  },
  data () {
    return {
      loading: false,
      submitted: false,
      bulkData: {
        role_id: '',
        quantity: 1,
        password: ''
      },
      modal: false
    }
  },
  computed: {
    ...mapState('users', {
      roles: 'roles'
    }),
    availableRoles () {
      // Filtrar apenas master, operator e viewer para criação em massa
      const allowedRoles = ['master', 'operator', 'viewer']
      return this.roles.filter(role => allowedRoles.includes(role.name))
    },
    selectedRole () {
      return this.roles.find(r => r.id === this.bulkData.role_id)
    },
    emailExample () {
      if (!this.selectedRole) {
        return { first: '', second: '', third: '' }
      }

      const prefixes = {
        'admin': 'admin',
        'master': 'master',
        'operator': 'operador',
        'viewer': 'visualizador'
      }

      const prefix = prefixes[this.selectedRole.name] || this.selectedRole.name.toLowerCase()

      return {
        first: `${prefix}001@eventfy.com.br`,
        second: `${prefix}002@eventfy.com.br`,
        third: `${prefix}003@eventfy.com.br`
      }
    }
  },
  methods: {
    ...mapActions('users', {
      bulkCreateUsers: 'bulkCreate',
      getRoles: 'getRoles'
    }),
    async open () {
      this.modal = true
      this.loading = true

      // Carregar roles
      await this.loadRoles()

      this.loading = false
    },
    async loadRoles () {
      try {
        await this.getRoles()
      } catch (err) {
        console.error('Erro ao carregar roles:', err)
      }
    },
    selectRole (roleId) {
      this.bulkData.role_id = roleId
    },
    getRoleDescription (roleName) {
      const descriptions = {
        'master': 'Controle avançado do sistema com permissões personalizáveis para gerenciar eventos e funcionalidades principais.',
        'admin': 'Gerencia eventos e usuários com permissões personalizáveis por módulo.',
        'operator': 'Acesso limitado para operações do dia-a-dia como check-in e gerenciamento de convidados.',
        'viewer': 'Apenas visualização de dados, sem permissões de edição ou exclusão.'
      }
      return descriptions[roleName] || 'Perfil de usuário com permissões específicas.'
    },
    async saveBulk () {
      this.submitted = true

      // Validação manual
      if (!this.bulkData.role_id || !this.bulkData.quantity || !this.bulkData.password) {
        return
      }

      if (this.bulkData.quantity < 1 || this.bulkData.quantity > 100) {
        return
      }

      if (this.bulkData.password.length < 6) {
        return
      }

      this.loading = true

      try {
        const result = await this.bulkCreateUsers(this.bulkData)

        const createdCount = result.data?.created || 0
        const failedCount = result.data?.failed || 0

        let message = `Sucesso: ${createdCount} usuário(s) criado(s) com sucesso!`
        if (failedCount > 0) {
          message += ` ${failedCount} falhou(aram).`
        }

        successMessage(message, 3000)
        this.close()
        this.$emit('created')
      } catch (err) {
        const errorMsg = err.response?.data?.message || err.message
        errorMessage('Alerta: ' + errorMsg)
      } finally {
        this.loading = false
      }
    },
    close () {
      this.submitted = false
      this.modal = false

      // Resetar dados
      this.bulkData = {
        role_id: '',
        quantity: 1,
        password: ''
      }
    }
  },
  watch: {
    modal () {
      if (!this.modal) {
        this.close()
      }
    }
  }
}
</script>
<style scoped>
.vld-overlay.is-full-page {
    z-index: 1051 !important;
}

label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 6px;
}

.form-control {
  font-size: 0.875rem;
  border-color: #e9ecef;
}

.form-control:focus {
  border-color: #556ee6;
  box-shadow: 0 0 0 0.1rem rgba(85, 110, 230, 0.15);
}

.form-group {
  margin-bottom: 1.25rem;
}

.alert-info {
  background-color: #f0f8ff;
  border-color: #bee5eb;
  color: #0c5460;
  font-size: 0.875rem;
}

/* ========== OPÇÕES DE ROLES ========== */
.role-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
}

.role-option {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #ffffff;
}

.role-option:hover {
  border-color: #556ee6;
  background: #f8f9fe;
}

.role-selected {
  border-color: #556ee6;
  background: linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%);
  box-shadow: 0 0 0 1px #556ee6;
}

.role-header {
  margin-bottom: 6px;
}

.role-header strong {
  font-size: 0.875rem;
  color: #495057;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.role-description {
  margin: 0;
  font-size: 0.75rem;
  color: #74788d;
  line-height: 1.4;
}
</style>
